import { useState, useEffect, useLayoutEffect, useRef, useCallback } from 'react';

declare global {
  interface Window {
    turnstile?: {
      render: (container: string | HTMLElement, options: object) => string;
      getResponse: (widgetId: string) => string;
      reset: (widgetId: string) => void;
      remove: (widgetId: string) => void;
    };
  }
}

export default function Contact() {
  const [isVisible, setIsVisible] = useState(false);
  const [siteKey, setSiteKey] = useState<string | null>(null);
  const [turnstileToken, setTurnstileToken] = useState<string | null>(null);
  const turnstileRef = useRef<HTMLDivElement>(null);
  const widgetIdRef = useRef<string | null>(null);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    phone: '',
    service: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [turnstileMountReady, setTurnstileMountReady] = useState(false);
  const [turnstileScriptReady, setTurnstileScriptReady] = useState(false);
  const [turnstileError, setTurnstileError] = useState<string | null>(null);
  const tokenPollRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const sectionRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.1 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const envKey = import.meta.env.VITE_TURNSTILE_SITE_KEY;
    const base = import.meta.env.BASE_URL || '/kumachop/';
    const apiBase = base.endsWith('/') ? base + 'api/' : base + '/api/';
    fetch(apiBase + 'turnstile.php')
      .then((res) => res.json())
      .then((data) => {
        const key = (data as { site_key?: string; error?: string }).site_key;
        if ((data as { error?: string }).error && !key) {
          console.warn('turnstile.php:', (data as { error?: string }).error);
        }
        setSiteKey(key || envKey || null);
      })
      .catch(() => setSiteKey(envKey || null));
  }, []);

  useEffect(() => {
    if (!siteKey) {
      setTurnstileScriptReady(false);
      return;
    }
    const markReady = () => setTurnstileScriptReady(true);
    // 既存の Turnstile script は動的挿入で async 既定値になり得るため除去し、async/defer なしで付け直す
    document.querySelectorAll('script[src*="challenges.cloudflare.com/turnstile"]').forEach((el) => {
      el.remove();
    });
    const script = document.createElement('script');
    script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
    script.async = false;
    script.defer = false;
    script.onload = markReady;
    script.onerror = () => {
      console.error('Turnstile のスクリプトの読み込みに失敗しました');
      setTurnstileError('認証スクリプトを読み込めませんでした。通信環境をご確認ください。');
    };
    document.head.appendChild(script);
    return () => {
      script.onload = null;
      script.onerror = null;
      script.remove();
      setTurnstileScriptReady(false);
    };
  }, [siteKey]);

  const setTurnstileContainerRef = useCallback((node: HTMLDivElement | null) => {
    turnstileRef.current = node;
    setTurnstileMountReady(!!node);
  }, []);

  useLayoutEffect(() => {
    if (!siteKey || !turnstileMountReady || !turnstileScriptReady || !turnstileRef.current) return;

    let cancelled = false;
    setTurnstileError(null);

    const startTokenPoll = (widgetId: string) => {
      if (tokenPollRef.current) {
        clearInterval(tokenPollRef.current);
        tokenPollRef.current = null;
      }
      let tries = 0;
      tokenPollRef.current = setInterval(() => {
        tries += 1;
        if (cancelled || tries > 100) {
          if (tokenPollRef.current) clearInterval(tokenPollRef.current);
          tokenPollRef.current = null;
          return;
        }
        const t = window.turnstile?.getResponse?.(widgetId);
        if (t) {
          setTurnstileToken(t);
          if (tokenPollRef.current) clearInterval(tokenPollRef.current);
          tokenPollRef.current = null;
        }
      }, 200);
    };

    const renderWidget = () => {
      if (cancelled || !turnstileRef.current || !window.turnstile) return;
      try {
        if (widgetIdRef.current) {
          try {
            window.turnstile.remove(widgetIdRef.current);
          } catch (_) {}
          widgetIdRef.current = null;
        }
        const id = window.turnstile.render(turnstileRef.current, {
          sitekey: siteKey,
          theme: 'light',
          size: 'normal',
          callback: (token: string) => {
            setTurnstileToken(token);
            if (tokenPollRef.current) {
              clearInterval(tokenPollRef.current);
              tokenPollRef.current = null;
            }
          },
          'expired-callback': () => setTurnstileToken(null),
          'error-callback': (err?: unknown) => {
            setTurnstileToken(null);
            const code = err != null ? String(err) : 'unknown';
            console.error('Turnstile error-callback:', code);
            setTurnstileError(
              '認証ウィジェットでエラーが発生しました。Cloudflare のホスト名設定（ドメイン）とサイトキーを確認してください。'
            );
          },
        });
        widgetIdRef.current = id;
        startTokenPoll(id);
      } catch (err) {
        console.warn('Turnstile render error:', err);
        setTurnstileError('認証の表示に失敗しました。ページを再読み込みしてください。');
      }
    };

    // turnstile.ready() は script に async/defer があると使用不可。onload 後は render のみでよい。
    const run = () => {
      if (cancelled) return;
      if (window.turnstile?.render) {
        renderWidget();
      } else {
        const poll = () => {
          if (cancelled) return;
          if (window.turnstile?.render) {
            renderWidget();
          } else {
            requestAnimationFrame(poll);
          }
        };
        requestAnimationFrame(poll);
      }
    };
    run();

    return () => {
      cancelled = true;
      if (tokenPollRef.current) {
        clearInterval(tokenPollRef.current);
        tokenPollRef.current = null;
      }
      if (widgetIdRef.current && window.turnstile) {
        try {
          window.turnstile.remove(widgetIdRef.current);
        } catch (_) {}
        widgetIdRef.current = null;
      }
      setTurnstileToken(null);
    };
  }, [siteKey, turnstileMountReady, turnstileScriptReady]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (siteKey && !turnstileToken) {
      setSubmitStatus('error');
      return;
    }
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formDataToSend = new FormData();
      Object.entries(formData).forEach(([key, value]) => {
        formDataToSend.append(key, value);
      });
      if (turnstileToken) {
        formDataToSend.append('cf-turnstile-response', turnstileToken);
      }

      const base = import.meta.env.BASE_URL || '/kumachop/';
      const apiBase = base.endsWith('/') ? base + 'api/' : base + '/api/';
      const response = await fetch(apiBase + 'contact.php', {
        method: 'POST',
        body: formDataToSend,
        credentials: 'include'
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({
          name: '',
          email: '',
          company: '',
          phone: '',
          service: '',
          message: ''
        });
        setTurnstileToken(null);
        if (widgetIdRef.current && window.turnstile) {
          window.turnstile.reset(widgetIdRef.current);
        }
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const contactInfo = [
    {
      icon: 'ri-map-pin-line',
      title: '所在地',
      content: '青森県弘前市笹森町37−37'
    },
    {
      icon: 'ri-time-line',
      title: '営業時間',
      content: '平日 9:00-18:00'
    }
  ];

  return (
    <section id="contact" ref={sectionRef} className="py-20 bg-white relative overflow-hidden">
      {/* くまチョップ（黒）コンテンツの陰に大きくうっすら */}
      <div className="absolute left-1/4 bottom-1/4 z-0 w-72 h-72 opacity-20 pointer-events-none select-none hidden lg:block" aria-hidden style={{ transform: 'translate(-20%, 15%)' }}>
        <img src={`${import.meta.env.BASE_URL}kumachop-black.png`} alt="" className="w-full h-full object-contain" />
      </div>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className={`text-center mb-16 transform transition-all duration-1000 ${
          isVisible ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'
        }`}>
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
            お問い合わせ
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            プロジェクトのご相談やお見積もりなど、お気軽にお問い合わせください
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
          {/* Contact Info */}
          <div className={`transform transition-all duration-1000 ${
            isVisible ? 'translate-x-0 opacity-100' : '-translate-x-10 opacity-0'
          }`}>
            <h3 className="text-2xl font-bold text-gray-900 mb-8">
              お気軽にご連絡ください
            </h3>
            <p className="text-gray-600 mb-8 leading-relaxed">
              Webサイト制作からデジタルマーケティングまで、
              お客様のビジネス成長をサポートいたします。
              まずはお気軽にご相談ください。
            </p>

            <div className="space-y-6">
              {contactInfo.map((info, index) => (
                <div
                  key={index}
                  className={`flex items-start space-x-4 transform transition-all duration-1000 ${
                    isVisible ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'
                  }`}
                  style={{ transitionDelay: `${index * 100}ms` }}
                >
                  <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center flex-shrink-0">
                    <i className={`${info.icon} text-xl text-blue-600`}></i>
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-1">
                      {info.title}
                    </h4>
                    <p className="text-gray-600">
                      {info.content}
                    </p>
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-8 p-6 bg-blue-50 rounded-2xl">
              <h4 className="font-bold text-gray-900 mb-2">
                無料相談実施中
              </h4>
              <p className="text-gray-600 text-sm">
                プロジェクトの規模に関わらず、まずはお気軽にご相談ください。
                お客様のご要望をお聞きし、最適なソリューションをご提案いたします。
              </p>
            </div>
          </div>

          {/* Contact Form */}
          <div className={`transform transition-all duration-1000 ${
            isVisible ? 'translate-x-0 opacity-100' : 'translate-x-10 opacity-0'
          }`}>
            <form id="contact-form" onSubmit={handleSubmit} className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="name" className="block text-sm font-semibold text-gray-700 mb-2">
                    お名前 <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-300 text-sm"
                    placeholder="山田太郎"
                  />
                </div>
                <div>
                  <label htmlFor="email" className="block text-sm font-semibold text-gray-700 mb-2">
                    メールアドレス <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-300 text-sm"
                    placeholder="example@email.com"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="company" className="block text-sm font-semibold text-gray-700 mb-2">
                    会社名・団体名
                  </label>
                  <input
                    type="text"
                    id="company"
                    name="company"
                    value={formData.company}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-300 text-sm"
                    placeholder="株式会社サンプル"
                  />
                </div>
                <div>
                  <label htmlFor="phone" className="block text-sm font-semibold text-gray-700 mb-2">
                    電話番号
                  </label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-300 text-sm"
                    placeholder="090-1234-5678"
                  />
                </div>
              </div>

              <div>
                <label htmlFor="service" className="block text-sm font-semibold text-gray-700 mb-2">
                  ご希望のサービス
                </label>
                <select
                  id="service"
                  name="service"
                  value={formData.service}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-300 text-sm pr-8"
                >
                  <option value="">選択してください</option>
                  <option value="website">Webサイト制作</option>
                  <option value="online">オンライン配信サポート</option>
                  <option value="signage">デジタルサイネージ</option>
                  <option value="design">グラフィックデザイン</option>
                  <option value="other">その他</option>
                </select>
              </div>

              <div>
                <label htmlFor="message" className="block text-sm font-semibold text-gray-700 mb-2">
                  お問い合わせ内容 <span className="text-red-500">*</span>
                </label>
                <textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleInputChange}
                  required
                  rows={6}
                  maxLength={500}
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-300 text-sm resize-none"
                  placeholder="プロジェクトの詳細やご要望をお聞かせください（500文字以内）"
                ></textarea>
                <div className="text-right text-sm text-gray-500 mt-1">
                  {formData.message.length}/500文字
                </div>
              </div>

              {siteKey && (
                <div className="space-y-2">
                  <div ref={setTurnstileContainerRef} className="min-h-[65px] flex items-center" />
                  {turnstileError && (
                    <p className="text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
                      {turnstileError}
                    </p>
                  )}
                </div>
              )}

              {submitStatus === 'success' && (
                <div className="p-4 bg-green-50 border border-green-200 rounded-xl">
                  <p className="text-green-800 text-sm">
                    お問い合わせありがとうございます。内容を確認次第、ご連絡いたします。
                  </p>
                </div>
              )}

              {submitStatus === 'error' && (
                <div className="p-4 bg-red-50 border border-red-200 rounded-xl">
                  <p className="text-red-800 text-sm">
                    {siteKey && !turnstileToken
                      ? '送信の前に、上の「私はロボットではありません」等の認証を完了してください。'
                      : '送信に失敗しました。しばらく時間をおいて再度お試しください。'}
                  </p>
                </div>
              )}

              <button
                type="submit"
                disabled={isSubmitting || formData.message.length > 500}
                className="w-full bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 text-white px-8 py-4 rounded-xl font-semibold transition-all duration-300 transform hover:scale-105 disabled:hover:scale-100 whitespace-nowrap cursor-pointer disabled:cursor-not-allowed"
              >
                {isSubmitting ? '送信中...' : 'お問い合わせを送信'}
              </button>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}
